import {
  isItemStatus,
  isUuid,
  type CreateItemRequest,
  type ItemStatus,
  type QueueItemPoll,
} from '@pink/shared';

import { ApiError, ApiErrorKind } from './api-error.js';

const QUEUE_BASE_PATH = '/v1/pink-queue';
const JSON_CONTENT_TYPE = 'application/json';

const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_SERVICE_UNAVAILABLE = 503;

interface CreateQueueItemOptions {
  readonly tenantId: string;
  readonly signal?: AbortSignal;
}

interface GetQueueItemOptions {
  readonly tenantId: string;
  readonly id: string;
  readonly signal?: AbortSignal;
}

/**
 * Erzeugt einen Queue-Eintrag (`POST /v1/pink-queue/:tenantId/termin-absage`).
 *
 * Spec-Kontrakt: Server antwortet 201 (Eintrag neu) oder 200 (idempotenter
 * Treffer, identische `absage_id` + Payload) — wir mappen beides auf einen
 * gemeinsamen Erfolgspfad und liefern den `QueueItemPoll`. Bei 409
 * (abweichender Payload zur gleichen `absage_id`) wirft die Funktion ein
 * `ApiError({kind: Conflict})`, weil das semantisch ein anderer Fehlerpfad
 * für den Caller ist.
 */
export async function createQueueItem(
  body: CreateItemRequest,
  options: CreateQueueItemOptions,
): Promise<QueueItemPoll> {
  if (!isUuid(options.tenantId)) {
    throw new ApiError({ kind: ApiErrorKind.SchemaInvalid });
  }
  const response = await fetchOrThrow(`${QUEUE_BASE_PATH}/${options.tenantId}/termin-absage`, {
    method: 'POST',
    headers: { 'Content-Type': JSON_CONTENT_TYPE },
    body: JSON.stringify(body),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  assertOkOrThrow(response);
  return parseQueueItemPoll(await readJsonOrThrow(response));
}

/**
 * Liest den aktuellen Status eines Queue-Eintrags
 * (`GET /v1/pink-queue/:tenantId/termin-absage/:id`).
 *
 * Im `pending`-Status fehlt `payload`; im terminal-Status `success`/`error`
 * kann der Server eine patient-verschlüsselte `payload`-Response mitgeben
 * (Spec-Erweiterung Pilot-Stack). Der Caller entscheidet, ob er den
 * Payload entschlüsselt.
 */
export async function getQueueItem(options: GetQueueItemOptions): Promise<QueueItemPoll> {
  if (!isUuid(options.tenantId) || !isUuid(options.id)) {
    throw new ApiError({ kind: ApiErrorKind.SchemaInvalid });
  }
  const response = await fetchOrThrow(
    `${QUEUE_BASE_PATH}/${options.tenantId}/termin-absage/${options.id}`,
    {
      method: 'GET',
      // Queue-Backend ist Cookie-frei (Spec: keine Pflicht-Auth, kein
      // implizites Session-Cookie). Cross-Origin-Mitlauf bewusst verhindert.
      credentials: 'omit',
      ...(options.signal ? { signal: options.signal } : {}),
    },
  );
  assertOkOrThrow(response);
  return parseQueueItemPoll(await readJsonOrThrow(response));
}

async function fetchOrThrow(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    if (isAbortError(cause)) {
      throw cause;
    }
    throw new ApiError({ kind: ApiErrorKind.Network, cause });
  }
}

function assertOkOrThrow(response: Response): void {
  if (response.ok) {
    return;
  }
  if (response.status === HTTP_NOT_FOUND) {
    throw new ApiError({ kind: ApiErrorKind.NotFound });
  }
  if (response.status === HTTP_CONFLICT) {
    throw new ApiError({ kind: ApiErrorKind.Conflict });
  }
  if (response.status === HTTP_PAYLOAD_TOO_LARGE) {
    throw new ApiError({ kind: ApiErrorKind.PayloadTooLarge });
  }
  if (response.status === HTTP_SERVICE_UNAVAILABLE) {
    throw new ApiError({ kind: ApiErrorKind.ServiceUnavailable });
  }
  if (response.status === HTTP_TOO_MANY_REQUESTS) {
    const retryAfterSeconds = readRetryAfterSeconds(response);
    throw new ApiError({
      kind: ApiErrorKind.RateLimited,
      ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
    });
  }
  throw new ApiError({ kind: ApiErrorKind.Network });
}

/**
 * Liest den `Retry-After`-Header einer 429-Antwort (T-9). Nur das
 * Sekunden-Format wird ausgewertet; die HTTP-Datum-Variante ist vom
 * Queue-Stack nicht zu erwarten und fällt auf `undefined` zurück.
 */
function readRetryAfterSeconds(response: Response): number | undefined {
  const raw = response.headers.get('retry-after');
  if (raw === null || !/^\d+$/u.test(raw.trim())) {
    return undefined;
  }
  return Number.parseInt(raw, 10);
}

async function readJsonOrThrow(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch (cause) {
    throw new ApiError({ kind: ApiErrorKind.SchemaInvalid, cause });
  }
}

function parseQueueItemPoll(payload: unknown): QueueItemPoll {
  if (typeof payload !== 'object' || payload === null) {
    throw new ApiError({ kind: ApiErrorKind.SchemaInvalid });
  }
  const record = payload as Record<string, unknown>;
  if (
    typeof record.id !== 'string' ||
    !isUuid(record.id) ||
    typeof record.absage_id !== 'string' ||
    !isUuid(record.absage_id) ||
    typeof record.status !== 'string' ||
    !isItemStatus(record.status) ||
    typeof record.created_at !== 'string' ||
    typeof record.updated_at !== 'string'
  ) {
    throw new ApiError({ kind: ApiErrorKind.SchemaInvalid });
  }
  const optionalPayload = record.payload;
  if (optionalPayload !== undefined && typeof optionalPayload !== 'string') {
    throw new ApiError({ kind: ApiErrorKind.SchemaInvalid });
  }
  const result: QueueItemPoll = {
    id: record.id,
    absage_id: record.absage_id,
    status: record.status as ItemStatus,
    created_at: record.created_at,
    updated_at: record.updated_at,
    ...(typeof optionalPayload === 'string' ? { payload: optionalPayload } : {}),
  };
  return result;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
